/**
 * AnalyticsFiltersHeader — global filter bar for the Analytics page.
 *
 * Dimensions: Owner, Product, Certificate, Certificate status, SCAN status, Time (date
 * range), Location. All seven are rendered in the DRAWER (§28 — one canonical field per
 * dimension). Three of them — the two status axes and the date range — ALSO get a
 * first-class control in the toolbar row itself (`ActionLine`'s `afterSearchComponents`
 * slot), which is a second ACCESS PATH onto the same redux state, not a second copy of
 * it: `AnalyticsStatusToolbarFilter`, `AnalyticsScanStatusToolbarFilter` and
 * `dateRangeControl` below all write straight into `state.analytics.filters`, exactly
 * what the drawer's own fields for those three dimensions write.
 *
 * ⚠️ CERTIFICATE STATUS AND SCAN STATUS ARE TWO DIMENSIONS, NOT ONE. A certificate is
 * Created / Active / Lost / Stolen; a scan is Verified / Invalid / Opened. They are
 * orthogonal and they AND together — "invalid scans of active certificates" is the query
 * this page exists to answer, and it needs both. Merged into a single multi-select it
 * would be inexpressible: as an AND that is the useful query, as an OR it is a union of
 * two unrelated sets, and one control cannot say which it did.
 *
 * ⚠️ TOOLBAR WHEN IT FITS, DRAWER AS THE FALLBACK HOME (VERA-260, responsive drop). The
 * two status axes and the date range are the most-reached-for facets, so they get toolbar
 * controls — but the toolbar is finite width and this row does NOT wrap (see
 * the staged thresholds below): on a narrow viewport the controls DROP out of the toolbar
 * entirely, in stages, rather than pushing the row onto a second line. They stay fully editable in
 * the drawer regardless — see the date range drawer field's own note for why that field
 * exists at all now (it did not, before this change).
 *
 * ⚠️ THERE IS ONLY ONE ROW NOW. The date picker used to occupy a full-width row above the
 * action line; see `dateRangeControl` below for why that row was a residue rather than a
 * design, and for why moving the picker down is what makes the ROI hero's fixed "All time"
 * an honest statement rather than a scope lie.
 *
 * ALL dimensions are wired end-to-end — the stat boxes, the time chart and the scans map each
 * constrain on every one of them.
 *
 * ⚠️ The Scans / Certificates toggle is NOT here any more. It scoped only the two panels,
 * so it now lives in each of their headers as `AnalyticsDimensionToggle`; see the note
 * where it used to sit, below the imports.
 *
 * Built as a standalone component so it can be reused on the certificates pages later.
 * TODO(VERA-260): drag-to-filter on the time graph (slice 4) and draw-to-filter on the map (slice 3).
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import dayjs, { Dayjs } from 'dayjs'

import { Box, InputAdornment, Stack, Tooltip, Typography, type Theme } from '@mui/material'

import { Button } from '@repo/ui'
import {
  DateTimeRangePicker,
  SingleInputDateTimeRangeField,
  type DateRange,
} from '@mui/x-date-pickers-pro'
import { CalendarIcon, DownloadIcon } from 'lucide-react'

import { DEFAULT_COMPANY_ID } from 'config'
// The shared disable-and-explain gate (§27), used on the Download button below.
import { PermissionGate } from 'components/access'
import { useDispatch, useSelector } from 'store/hooks'
import { setAnalyticsFilters } from 'store/analytics/actions'
import { BOOTSTRAP_DATE_RANGE, billingPeriodDateRange } from 'store/analytics/reducer'
import { useOwners, useProducts, useCertificates, useCountriesQuery, useCompany } from 'hooks/api'
import { ActionLine, type FilterOpenRequest } from 'components/input/ActionLine'
// The shared container-query mechanism (VRG-535) — see the note above
// `TOOLBAR_SCAN_STATUS_MIN_WIDTH_PX` for why the helpers moved out of this file and the
// thresholds stayed in it.
import { hideAtLeast, showAtLeast, toolbarLabelSx } from 'components/input/ActionLine/toolbarCollapse'
import {
  AnalyticsSearchTypeahead,
  type AnalyticsSearchFilterField,
  type AnalyticsSearchGroup,
} from './AnalyticsSearchTypeahead'
import { AnalyticsStatusToolbarFilter } from './AnalyticsStatusToolbarFilter'
import { AnalyticsScanStatusToolbarFilter } from './AnalyticsScanStatusToolbarFilter'
import { AnalyticsDateRangeDrawerField } from './AnalyticsDateRangeDrawerField'
import { AnalyticsAreaDrawerField } from './AnalyticsAreaDrawerField'
import {
  CERT_STATUS_OPTIONS,
  FILTER_FIELDS,
  SCAN_STATUS_OPTIONS,
  collapseStatuses,
  expandStatuses,
} from './analyticsFilterFields'
import {
  DATE_RANGE_FIELD_FORMAT,
  DATE_TIME_RANGE_FIELD_FORMAT,
  DATE_FIELD_GAP,
  DATE_FIELD_ICON_SIZE,
  DateRangeCalendarHeader,
  DateRangePickerDay,
  dateRangeDaySx,
  dateRangeDesktopPaperSx,
  dateRangeFieldWithIconSx,
  dateRangeShortcutsSx,
  dateRangeTextWidth,
  makeDateRangeShortcutItems,
  matchDateRangeShortcut,
  isWholeDayRange,
} from 'components/input/filters/dateRangeChrome'

/**
 * ── RESPONSIVE DROP, NOT WRAP (Task 2, VERA-260; re-based on the CONTAINER, VRG-517) ──
 *
 * Applied to the toolbar's two status controls and the date-range control — per user
 * directive, the items that remain "still accessible in the filter sidebar" if they
 * disappear. Below their threshold they render `display: none` entirely; at or above it
 * they render normally. An applied value never becomes invisible when its control drops:
 * the drawer always carries the field, and `AnalyticsAppliedFilters` shows a fallback chip
 * that appears exactly when the control goes (see `hideAtLeast`).
 *
 * ══ THE THRESHOLDS ARE CONTAINER QUERIES, NOT MEDIA QUERIES ══════════════════════════
 *
 * 🚨 THEY WERE MEDIA QUERIES, AND THAT WAS A BUG RATHER THAN A SIMPLIFICATION. The version
 * that shipped keyed on the VIEWPORT and justified it with this claim:
 *
 *     "band width = viewport - 129 — an offset that holds EXACTLY at every width measured
 *      and is INDEPENDENT of the sidebar's expanded/collapsed state, because the drawer
 *      expands as an overlay and the main column is always offset by
 *      SIDEBAR_MINIMIZED_WIDTH."
 *
 * The sidebar is NOT an overlay. It is a flex sibling of `main` with a real width — see
 * `Sidebar.tsx` (`openedMixin` 288 / `closedMixin` 80, applied to the drawer ROOT) and
 * `Layout.tsx`, which puts `main` beside it with `flexGrow: 1` — so expanding it takes
 * 208px out of this band. Measured at a 1280 viewport: `main` is 1199 collapsed and 991
 * expanded. The real offset is 129 collapsed and 337 EXPANDED, so every viewport
 * threshold was ~208px too generous whenever the sidebar was open.
 *
 * That is precisely the reported failure. At a 1560 viewport with the sidebar expanded the
 * media query switched the date control ON with 1223px of band available, against the
 * 1320 that configuration needs — measured band 1223, height 127, i.e. TWO rows, which is
 * the exact outcome this whole mechanism exists to prevent.
 *
 * A CSS **container query** takes the offset out of the problem instead of correcting it:
 * the controls respond to the width of the band they are actually in, so the sidebar, the
 * Ask panel and any future chrome are all accounted for with no constant to keep in sync.
 * It keeps the one property that made a media query the right instrument here — it is
 * evaluated by the browser's own layout engine, so unlike `ResizeObserver` it works inside
 * the Claude preview pane. (Verified there on this row: a `ResizeObserver` fired ZERO
 * times across a real viewport resize, as did `window`'s `resize` event and `matchMedia`'s
 * `change` event, while `matchMedia(...).matches` still read correctly on demand.)
 *
 * The container itself is declared by `toolbarContainerSx` below.
 *
 * ── STAGED DROP: SCAN STATUS, THEN CERTIFICATE STATUS, THEN THE DATE ─────────────
 *
 * One threshold for all three would be wrong, because they are not equally important. The
 * date range scopes EVERY number on the page; certificate status narrows one facet across
 * the whole page; scan status narrows one facet on ONE card (the map — see
 * `AnalyticsScanStatusToolbarFilter` for why the server can only honour it there). So they
 * drop in ascending order of reach rather than the row losing all three at once (user
 * directive, extended to the third control).
 *
 * Each threshold describes the configuration that exists AT its width, never a cumulative
 * total: under `TOOLBAR_SCAN_STATUS_MIN_WIDTH_PX` the scan control is already
 * `display: none`, so the next threshold down describes a three-item row.
 *
 * ── THE NUMBERS, RE-MEASURED AS CONTAINER WIDTHS (VRG-517) ───────────────────────
 *
 * Found by BISECTION at 2px in the browser: pin the viewport (so the configuration under
 * test stays fixed, since the old media queries still governed which controls were shown
 * while these were being taken) and sweep `main`'s width. Band height 66 = one row,
 * 127 = two rows.
 *
 *   configuration                        container crossover   constant
 *   search + date + view-by + cert + scan        1888            1904
 *   search + date + cert + scan                  1724            1740
 *   search + date + cert                         1544            1560
 *   search + date                                1320            1336
 *   search only — the FLOOR, nothing left         924             —
 *     to drop below it
 *
 * Each constant carries 16px of headroom over its measured crossover. The differences are
 * self-checking against the control widths: 1888 - 1724 = 164 ≈ the view-by control's 154px
 * plus the row's 12px gap (the 2px shortfall is the bisection step), 1724 - 1544 = 180 =
 * the scan control's 169px plus 12, and 1544 - 1320 = 224 = the cert control's 211px plus 12.
 *
 * Contributing widths, `getBoundingClientRect`, date in its widest (billing-period) form:
 *
 *   search + scan  406px
 *   date range     385px
 *   cert status    211px
 *   scan status    169px
 *   view by        154px   at "View by: Month"; the fallback caption makes it WIDER
 *   right group    513px   at its FLOOR (Download / Overview / Filter / Layout + gaps)
 *
 * ⚠️ THE VIEW-BY ROW WAS MEASURED, AND MEASURING IT IS WHAT CAUGHT THE BUG (VRG-512). The
 * control first shipped sharing the DATE's 1336 stage, on the reasoning that the two belong
 * together — which is true of their POSITION and false of their WIDTH. Bisecting found the
 * row two rows tall (127px) in every one of the three bands below 1888: each constant above
 * carries 16px of headroom and the control needs 154, so there was no stage it fitted into.
 * At 1280 it did not even wrap — it was `display: none` with no chip behind it, so the whole
 * feature was silently unreachable at the width it is verified at. Exactly the failure the
 * "RE-MEASURE, DO NOT RE-REASON" warning below is about, and it was found by sweeping the
 * container, not by reading the numbers.
 *
 * ⚠️ THE RIGHT GROUP IS ELASTIC, which is why the crossover cannot be computed from a
 * single measurement of it: it renders 1571px at a 1920 viewport and compresses to 513
 * before the row gives up and wraps. Only the FLOOR predicts a crossover; reading its
 * relaxed width puts the threshold far too high.
 *
 * ⚠️ THE FLOOR IS 924 AND THE NARROWEST SUPPORTED LAYOUT CLEARS IT BY 19px. A 1280
 * viewport (§37's desktop verification width) with the sidebar EXPANDED leaves this band
 * 943px, so it fits — but there is no fourth stage beneath it. If the search typeahead
 * grows, and its 406px is a measurement on one company's data rather than a constant of
 * the design, that configuration wraps and the only thing left to drop is the search
 * landmark, which is not a candidate (see `afterSearchComponents` in `ActionLine`).
 *
 * ⚠️ RE-MEASURE, DO NOT RE-REASON, if this row gains or loses a control or a label
 * changes. Every value in this block has been wrong at some point, and on both occasions
 * the error came from DERIVING a threshold from control widths instead of bisecting for
 * it. The failure is silent: the controls still render, the row simply becomes two rows
 * tall.
 */
/**
 * ⚠️ THE CONTAINER AND THE `showAtLeast` / `hideAtLeast` HELPERS MOVED (VRG-535).
 *
 * They used to be declared here, under the name `analytics-toolbar`. The same mechanism was
 * then needed for the ordinary table toolbar's own label collapse — the SECOND hand-roll,
 * which is the extraction trigger (working-conventions §36) — so it now lives in
 * `components/input/ActionLine/toolbarCollapse.ts` and is imported rather than copied.
 *
 * TWO THINGS CHANGED HERE AND BOTH MATTER:
 *
 * 1. THE NAME IS NOW `vera-toolbar`, shared. `ActionLine` declares the container on its own
 *    root, so it is no longer this file's job to pass one in — and two container names on
 *    one element would mean whichever `sx` landed last silently won.
 * 2. THE CHIP ROW STILL DECLARES IT. `AnalyticsAppliedFilters` is a SIBLING of the toolbar,
 *    not a descendant, so it needs its own container for the `*ChipInverseSx` queries below
 *    to resolve. Same width, so a rule written against one resolves identically in the
 *    other — which is what keeps a fallback chip from being on screen at the same time as
 *    the control it stands in for. Without a matching ancestor the query never matches and
 *    every chip renders permanently, at every width.
 *
 * The THRESHOLDS stay here, beside the measurements they came from: they describe this row,
 * not the shared mechanism.
 */
export { TOOLBAR_CONTAINER_NAME, toolbarContainerSx } from '../input/ActionLine/toolbarCollapse'

/**
 * ⚠️ `TOOLBAR_GRANULARITY_MIN_WIDTH_PX = 1904` WAS HERE AND IS DELETED (VRG-512).
 *
 * "View by" was a page-level control, so it needed a stage of its own in this row — and at
 * 154px the only stage that fitted was above every laptop, which is why it also needed a
 * fully-working fallback chip rather than a read-only one. The bucket is now a per-card
 * display property rendered in each chart card's header
 * (`AnalyticsCardGranularityControl`), so there is no toolbar control to stage and no chip
 * to stand in for it.
 *
 * ⚠️ THE THREE BELOW DID NOT MOVE, and that is a property of the removal rather than luck.
 * The granularity stage was deliberately placed FIRST-to-drop precisely so every
 * configuration beneath it stayed byte-identical to the four-control row that predates it —
 * which made its deletion a single-constant deletion, exactly as its addition was a
 * single-constant addition. Re-verified after removal: 1740 / 1560 / 1336, unchanged.
 */
/** Scan status — first to go among the three: narrowest reach (one card). */
export const TOOLBAR_SCAN_STATUS_MIN_WIDTH_PX = 1740
/** Certificate status — held longer: it narrows one facet across the whole page. */
export const TOOLBAR_STATUS_MIN_WIDTH_PX = 1560
/** The date control — held longest, because it scopes every number on the page. */
export const TOOLBAR_DATE_MIN_WIDTH_PX = 1336

export const TOOLBAR_SCAN_STATUS_DROP_SX = showAtLeast(TOOLBAR_SCAN_STATUS_MIN_WIDTH_PX)
export const TOOLBAR_STATUS_DROP_SX = showAtLeast(TOOLBAR_STATUS_MIN_WIDTH_PX)
export const TOOLBAR_DATE_DROP_SX = showAtLeast(TOOLBAR_DATE_MIN_WIDTH_PX)

/**
 * The exact inverses, for the fallback chips in `AnalyticsAppliedFilters.tsx`.
 *
 * Each chip appears exactly when ITS OWN control disappears, so one of each pair is always
 * on screen and never both. They are functions only because the shown display differs — the
 * toolbar controls are `flex`, a `Chip` is naturally `inline-flex` and forcing `flex` would
 * change its layout. The THRESHOLDS, the part that must never diverge, still come from the
 * three constants above: a chip writing its own query would open a width band rendering
 * both (visible duplication) or neither (the filter's value silently invisible).
 *
 * ⚠️ These resolve against `TOOLBAR_CONTAINER_NAME`, so the chip row MUST declare the
 * container too (it spreads `toolbarContainerSx` on its own root). Without a matching
 * ancestor the query simply never matches and every chip would render permanently — the
 * duplication case above, at every width.
 */
/**
 * ⚠️ `granularityChipInverseSx` WAS HERE AND IS DELETED WITH ITS THRESHOLD (VRG-512).
 *
 * It was the one INVERSE that fronted a working control rather than a read-only label,
 * because its threshold (1904) put the toolbar control off screen at every width this page
 * is actually used at — so the chip was the feature, not a degraded stand-in for it. Both
 * halves are gone: the bucket now lives in each chart card's own header, which is on screen
 * at every width, so there is nothing left for a chip to substitute for.
 */
export const statusChipInverseSx = (shown: 'flex' | 'inline-flex' = 'inline-flex') =>
  hideAtLeast(TOOLBAR_STATUS_MIN_WIDTH_PX, shown)
export const scanStatusChipInverseSx = (shown: 'flex' | 'inline-flex' = 'inline-flex') =>
  hideAtLeast(TOOLBAR_SCAN_STATUS_MIN_WIDTH_PX, shown)
export const dateChipInverseSx = (shown: 'flex' | 'inline-flex' = 'inline-flex') =>
  hideAtLeast(TOOLBAR_DATE_MIN_WIDTH_PX, shown)

// ---------------------------------------------------------------------------
// ⚠️ THE SCANS / CERTIFICATES PILL USED TO LIVE HERE, AND IT IS NOT COMING BACK
// ---------------------------------------------------------------------------
//
// A `SegmentedTabs` sat immediately left of the date picker — back when the picker had a
// full-width row of its own above the action line (that row is now gone too; see
// `dateRangeControl`) — writing a single
// page-level `filters.dataType`. It was a PER-CARD control promoted to global: it had
// local scope (the time chart and the location map, and nothing else — the four KPI
// tiles ignored it entirely) and global position, beside a date range that genuinely
// does scope every number on the page, with nothing on screen telling the two apart.
//
// It now renders inside each panel's own header, as `AnalyticsDimensionToggle`, against
// two independent redux keys. The full argument, including why they must NOT be
// re-linked, is on `AnalyticsFilters` in `store/analytics/types`.
//
// That left the date picker alone on its row, which was the honest CONTENT of "controls
// that scope the whole page" but the wrong SHAPE for it — one control on a full-width line
// reads as an orphan, not as a first-class control. So the row went and the picker joined
// the toolbar; see `dateRangeControl`.

/**
 * The date-range field, with MUI's section padding turned off.
 *
 * Without `shouldRespectLeadingZeros` the field renders `08:05 PM` for the `h`
 * (no-leading-zero) token in `DATE_TIME_RANGE_FIELD_FORMAT`. MUI pads every editable
 * section by default so the caret width never changes as you type through it — sensible
 * for a field being typed into, wrong for one that is read far more often than edited,
 * and it makes a 12-hour clock look like a 24-hour one at a glance.
 *
 * WHY A WRAPPER AND NOT A PROP. There are two places to set it and neither works
 * directly. On the PICKER it is silently ignored: unlike `format`, the picker does not
 * forward it to the field (verified in the browser — the field went back to `08:05 PM`).
 * In `slotProps.field` it does work, but that slot's props are typed against the picker's
 * DEFAULT field — the MULTI-input one — no matter which component `slots.field` actually
 * receives, so setting anything there is a type error. Presetting it on the component
 * itself is the one path that is both effective and typed, and it needs no cast.
 *
 * Module-level so its identity is stable: a component defined inside the render would be
 * a new type every pass and remount the field on every keystroke.
 */
const UnpaddedDateTimeRangeField = Object.assign(
  /**
   * `forwardRef`, not a plain function. The picker hands the field a ref (it needs the DOM
   * node to anchor the popper and to restore focus on close), and a plain function component
   * cannot hold one — React logs "Function components cannot be given refs" and the ref is
   * silently dropped.
   */
  React.forwardRef<HTMLDivElement, React.ComponentProps<typeof SingleInputDateTimeRangeField>>(
    (props, ref) => (
      <SingleInputDateTimeRangeField {...props} ref={ref} shouldRespectLeadingZeros />
    ),
  ),
  {
    /**
     * MUST be copied onto the wrapper. The picker decides whether it is driving ONE input
     * or a start/end PAIR by reading this static off the field component, not by inspecting
     * what it renders. Without it the wrapper reads as a multi-input field: the control
     * sprouted a floating "End" label, lost its 49px chrome, and stopped honouring the
     * container width. `SingleInputDateTimeRangeField` carries it; a plain arrow function
     * wrapping it does not.
     */
    fieldType: SingleInputDateTimeRangeField.fieldType,
  },
)

// ---------------------------------------------------------------------------
// Redux <-> Filters adapter
// ---------------------------------------------------------------------------

/**
 * `FILTER_FIELDS`, `CERT_STATUS_OPTIONS` and the `expandStatuses` / `collapseStatuses`
 * pair now live in `analyticsFilterFields` — see that module for why they moved
 * (`AnalyticsAppliedFilters` is their second consumer, and it needs the field list and
 * the status labels without pulling this component's date-picker chain with it).
 *
 * ## Why an adapter exists at all
 *
 * `Filters` is built for a DataGrid: its fields emit `{field, operator, value}` objects, it
 * stages them in an array, and it reports that whole array back through `onChange`. This page
 * does not have a grid — its selections live in redux (`state.analytics.filters`) and are sent
 * to the API as `owner_ids` / `product_ids` / `certificate_statuses` / … So the two ends speak
 * different shapes and something has to translate.
 *
 * The translation is deliberately the DUMBEST one available: a column's `field` IS its redux
 * key, so both directions are a rename-free lookup and there is no mapping table to drift.
 * `operator` is always `isAnyOf` (every one of these is a set membership test) and is discarded
 * on the way in — the analytics query has no operator concept.
 *
 * Status is the one field that is not identity, because the drawer shows ONE "Transfer" chip
 * while the API wants all four transfer statuses. That conversion is `expandStatuses` /
 * `collapseStatuses`, and it is the reason this adapter is a function rather than an
 * object spread.
 *
 * The alternative — the `extraFilters` slot with hand-rolled selects — is what this replaces.
 * That slot is for controls that are NOT columns (e.g. "Show archived"); using it for five
 * ordinary facets meant rebuilding, in this file, chips and search and clear behaviour the
 * drawer already implements, and the result did not match the drawer on any other page (§28).
 */

interface AnalyticsFiltersHeaderProps {
  /** Fires from the action bar's Download button. Format is deliberately the
   *  caller's choice — the control is generic "Download", not "Download CSV". */
  onDownload?: () => void
  /**
   * The export is running.
   *
   * The handler is ASYNC as of VERA-260 — the export re-issues a request per chart, map
   * and leaderboard on the canvas — so the button needs a busy state. Without one it is a
   * control that appears inert for a second or two, and the reader's response to an inert
   * control is to press it again, which starts a second export.
   *
   * `loading` on the shared `Button` rather than a bare `disabled`: a disabled button with
   * no explanation is the dead control §46 is about, whereas a spinner says which of the
   * two states this is.
   */
  downloadBusy?: boolean
  /**
   * Controls placed in the action bar immediately LEFT of Filter — Views.
   *
   * A pass-through rather than something this component builds, because it is bound to view
   * state the PAGE owns (`useAnalyticsView`), and this component owns the filter dimensions
   * only. It lands in `ActionLine`'s existing `beforeFiltersComponents` slot, so the toolbar
   * band, gaps and wrap behaviour are the shared ones every table gets rather than a second
   * arrangement here.
   *
   * ⚠️ The dashboard's "Layout" toggle (`AnalyticsCustomizeButton`) used to ride ALONG with
   * Views in this same slot — which put it to the LEFT of Filter, the one page whose Layout
   * control did not sit where Layout sits everywhere else (right of Filter, via the built-in
   * `showLayoutOptions`). It now has its own slot below (`afterFilters`).
   */
  beforeFilters?: React.ReactNode
  /**
   * Controls placed in the action bar immediately RIGHT of Filter — parity with every other
   * page's Layout control, which renders there via the built-in `showLayoutOptions`. Analytics
   * cannot use that prop directly (its Layout control edits the dashboard's card grid, not a
   * DataGrid's column visibility/order/density), so `ActionLine`'s `afterFiltersComponents`
   * slot exists for exactly this case — see that prop's own doc.
   */
  afterFilters?: React.ReactNode
  /**
   * Open the filter drawer at a named dimension. Driven by the applied-filter chip row,
   * which the PAGE renders BELOW the pinned band — see `AnalyticsAppliedFilters`
   * constraint 4 for why the chips cannot live inside this component.
   */
  filterOpenRequest?: FilterOpenRequest | null
}

export const AnalyticsFiltersHeader = ({
  onDownload,
  downloadBusy,
  beforeFilters,
  afterFilters,
  filterOpenRequest,
}: AnalyticsFiltersHeaderProps) => {
  const dispatch = useDispatch()
  const filters = useSelector(state => state.analytics.filters)
  /**
   * The date picker is OPEN-CONTROLLED, and has to be.
   *
   * `SingleInputDateRangeField` renders no `openPickerButton` adornment, and clicking its text
   * does not open the picker on its own — so before this the calendar could not be opened at
   * all. Verified in the browser on the pre-change version: clicking the field focused it and
   * left `.MuiPickersPopper-root` absent from the DOM, i.e. the range was only ever editable by
   * typing, and none of the shortcut presets were reachable.
   *
   * `DateRangePickerFilter` does not hit this because it already controls `open` for its own
   * Apply/Clear action bar; the same wiring is what this control was missing.
   */
  const [pickerOpen, setPickerOpen] = useState(false)

  /**
   * OWNER / PRODUCT / CERTIFICATE OPTIONS ARE THE WHOLE ENTITY, NOT `{id, label}`.
   *
   * They feed `SearchFilter`, which is the same field the certificates drawer renders for these
   * three dimensions — so `searchParams.preview` draws the real `OwnerPreview` / `ProductPreview`
   * / `CertificatePreview` row (avatar or product image, name, secondary line), and search runs
   * over the entity's own keys through `getValuesByKeys` (dot-paths supported, which is how a
   * certificate is searchable by `product.title`).
   *
   * Flattening to `{id, label}` — what the previous `multiSelect` version needed — would throw
   * that away: the preview components read `owner.avatar`, `product.images`, `certificate.product`,
   * none of which survive the flatten. Sorting is dropped for the same reason; the dropdown is
   * searched, not scanned, and these arrive in the API's own order.
   */
  // The company's own start instant — the floor for the "All time" preset. Same source
  // RoiHero uses, so the picker and the hero agree on what "all time" means.
  const { data: company } = useCompany({ id: DEFAULT_COMPANY_ID })

  const { data: ownersData } = useOwners({ size: 100 })
  const ownerOptions = useMemo(() => ownersData?.items ?? [], [ownersData])

  const { data: productsData } = useProducts({ limit: 100 })
  const productOptions = useMemo(() => productsData?.data ?? [], [productsData])

  const { data: certificatesData } = useCertificates({ size: 100 })
  const certificateOptions = useMemo(
    () =>
      // `CertificatePreview` reads `certificate.product.images[0]` WITHOUT guarding the array, so
      // a certificate whose product carries no `images` throws a TypeError during render and the
      // global ErrorBoundary takes the page out. The certificates LIST rows always carry it; a
      // synthesised or partially-hydrated row need not, and this dropdown is the first surface to
      // feed that component a row it did not fetch itself. Normalising here is the cheap half of
      // the fix — the guard belongs in the component, and is noted rather than made from this file.
      (certificatesData?.data ?? []).map(c =>
        c?.product && !Array.isArray((c.product as any).images)
          ? { ...c, product: { ...c.product, images: [] } }
          : c,
      ),
    [certificatesData],
  )

  /**
   * LOCATION OPTIONS — country-granular, and the VALUE IS THE COUNTRY NAME.
   *
   * ## The value was wrong, and the filter could never have matched
   *
   * This sent `c.code` — the ISO-2 code — while all four analytics queries compare
   * `scans.geo_country = ANY($n)` (`analytics.postgres.repository.ts`, the totals / granular /
   * scans-map / ROI builders), and the ONLY writer of that column is
   * `log-public-access.use-case.ts:114`, which stores `ReverseGeocodeResult.country` — the
   * geocoder's DISPLAY NAME ("France"), documented as such on the interface. `country_code` is
   * resolved alongside it and thrown away. So "FR" was being compared against "France" and the
   * Location filter silently narrowed to nothing on every selection.
   *
   * ## Why the options are still countries, when the ask was city + state + country
   *
   * There is no city or state predicate to select against. `geo_city` and `geo_state` are stored
   * on `scans` and returned on `IScanMapPoint`, but no analytics query filters on either — so a
   * city option would emit a value that matches zero rows, i.e. an empty page that looks like
   * "no data" rather than "unsupported". Shipping that is worse than not offering it.
   *
   * What IS delivered: the cities and states actually present in the loaded scan points are folded
   * into each country's `searchText`, so typing "Miami" or "Oregon" finds "United States". You
   * search by city or state; you filter by the country it is in, and the chip says so.
   *
   * ADDRESS is not available at any granularity — `scans` has no street-address column and
   * `IScanMapPoint` carries none. The geocoder's `formatted_address` is written only into
   * ACTION-LOG metadata (`update-scan-location.use-case.ts`), never onto the scans row the
   * analytics queries read.
   *
   * ## Where the list comes from
   *
   * Countries actually seen in the scan data lead (they are spelled exactly as stored, so they are
   * guaranteed to match), then the full catalogue fills in behind them so the field is never empty
   * before the map has loaded. Seen countries are RETAINED across filter changes — the scan points
   * are themselves narrowed by this filter, so deriving purely from them would remove every other
   * country the moment one was picked.
   */
  const { data: countriesData } = useCountriesQuery()
  /*
    ACROSS EVERY CACHED REQUEST, not one response — the scans-map slot is now keyed by
    request (`AnalyticsScansMapCache`), because the page can hold several map cards asking
    different questions.

    Flattening is the RIGHT reading here rather than a concession to the new shape: this
    list is an accumulating record of countries ever seen (see `seenGeoRef` below, which
    retains across filter changes for exactly that reason), so every cached response is
    evidence and none of them is the authoritative one. Picking a single entry would have
    meant picking an arbitrary card's answer.
  */
  const scansMapCache = useSelector(state => state.analytics.scansMap)
  const scanPoints = useMemo(
    () => Object.values(scansMapCache).flatMap(entry => entry?.data ?? []),
    [scansMapCache],
  )
  const seenGeoRef = useRef<Map<string, Set<string>>>(new Map())
  const locationOptions = useMemo(() => {
    for (const p of scanPoints ?? []) {
      const country = p.geo_country?.trim()
      if (!country) continue
      const bucket = seenGeoRef.current.get(country) ?? new Set<string>()
      if (p.geo_state) bucket.add(p.geo_state)
      if (p.geo_city) bucket.add(p.geo_city)
      seenGeoRef.current.set(country, bucket)
    }
    const seen = Array.from(seenGeoRef.current.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([country, places]) => ({
        value: country,
        label: country,
        // `country` is the row's RIGHT-hand text and `label` its left; repeating the name on both
        // sides of its own row is noise, so it stays empty (unchanged from before).
        country: '',
        searchText: Array.from(places).sort().join(', ') || undefined,
      }))
    const seenNames = new Set(seen.map(o => o.value))
    const rest = (countriesData ?? [])
      .filter(c => !seenNames.has(c.name))
      .map(c => ({ value: c.name, label: c.name, country: '' }))
    return [...seen, ...rest]
  }, [countriesData, scanPoints])

  /**
   * The drawer's OWN fields, one per dimension — `Filters` switches on `type` and renders
   * `SearchFilter` / `StatusChipFilter` / `LocationFilter` itself.
   *
   * ## Why Owner / Product / Certificate are `type: 'string'`
   *
   * They were `multiSelect`, which renders every option as a chip in a wrapped grid. That is the
   * right control for a short curated enum (Interval, Size, Capability — the nine other columns
   * in the app that use it) and the wrong one for an unbounded list of entities: a hundred
   * uuid-backed chips, no entity preview, and no scan affordance, because `MultiSelectFilter`
   * has none.
   *
   * `type: 'string'` dispatches `SearchFilter` — a searchable select with an NFC scan target
   * beside it — which is what the CERTIFICATES drawer already renders for these same three
   * dimensions (`CertificatesPage.tsx`, `certificate` / `product` / `owner`). So this is not a
   * new control; it is these columns declaring the type they always were, and inheriting the
   * field that type already resolves to.
   *
   * `MultiSelectFilter` is deliberately NOT changed to behave this way. Nine columns across
   * Invoices, Demos, Subscriptions, Tag shop, Inventory and the internal lists render it, and a
   * chip grid is correct for all of them.
   *
   * ## What `SearchFilter` needed to make that reuse possible
   *
   * Two opt-in column keys, both added there and both absent on every other column in the app:
   *   - `options` — this page has no grid, so the drawer's shared `rows` is `[]` and the field
   *     had nothing to list. Options come from three independent queries instead.
   *   - `scanEntity` — the field-name map keys on `owner` / `product` / `certificate`, and these
   *     fields are named for the API params they map to (`ownerIds`, …), so the scan control
   *     would have silently not rendered.
   *
   * Status stays `statusChip`: a fixed, colour-coded catalogue of six, where the chips ARE the
   * design and a search box over six items is friction. It ALSO gets a compact toolbar control
   * now (`AnalyticsStatusToolbarFilter`, VERA-260) — both write the same
   * `filters.certificateStatuses`, so this drawer field and the toolbar chip trigger are two
   * doors onto one room, never two rooms.
   *
   * ── THE DATE RANGE ─────────────────────────────────────────────────────────────────
   *
   * 🚨 UPDATED, VERA-260 — this used to say the date range belongs ONLY in the toolbar,
   * "on purpose", because "a filter you must open a panel to see is the wrong affordance for
   * the thing that defines the whole view." That reasoning still holds WHILE the toolbar has
   * room to show it — it is exactly why the date control keeps its place there (see
   * `dateRangeControl`, in `ActionLine`'s `afterSearchComponents` slot) whenever `TOOLBAR_DATE_DROP_SX`
   * allows it to render. What changed is that the toolbar can no longer be assumed to always
   * have that room: the responsive DROP (Task 2, VERA-260) hides the toolbar control below a
   * viewport width rather than letting the row wrap, and a filter that becomes both
   * toolbar-only AND sometimes-hidden would have no way to be set on a narrow screen at all.
   * So the rule is now: **toolbar when it fits, drawer as the fallback home, one shared
   * state** — it is a genuinely NEW field: the date range previously had no drawer
   * representation whatsoever.
   *
   * It is NOT a `columns` entry, though — see the comment on the `location` entry below
   * for why (the badge-count problem) — it is passed to `Filters` via the `extraFilters`
   * slot instead, as `AnalyticsDateRangeDrawerField` (own new file, own local `open` state
   * so it and the toolbar's `dateRangeControl` can both be mounted at once without fighting
   * over one Popper).
   */
  const columns = useMemo(
    () => [
      {
        field: FILTER_FIELDS.status,
        /**
         * "Certificate Status", not "Status" — this page counts TWO things.
         *
         * On a list of certificates, "Status" is unambiguous: there is one kind of row and one
         * status it can be in. This page is not a list. The toggle above the drawer switches the
         * whole surface between counting SCANS and counting CERTIFICATES, and a scan carries its
         * own status vocabulary (invalid / tampered / verified — `IScanMapPoint`, drawn as the
         * status legend beside the map). So a bare "Status" in the drawer names neither one and
         * is read as whichever the user currently has selected — which is wrong half the time.
         *
         * Two words, 18 characters. §25's ceiling is a matrix column and this is a drawer label,
         * but the brevity rule is the same one and this sits inside it; "Certificate activation
         * status" would not.
         *
         * Deliberately NOT renamed on the certificates or products drawers. There "Status" has
         * exactly one referent, so qualifying it would add a word that discriminates nothing.
         */
        headerName: 'Certificate Status',
        type: 'statusChip',
        options: CERT_STATUS_OPTIONS,
      },
      {
        field: FILTER_FIELDS.scanStatus,
        /**
         * "Scan Status" — the SECOND status axis (VERA-260), and it sits immediately after
         * Certificate Status on purpose. Two status facets adjacent, each naming its own
         * entity, is what makes the pair legible; either one labelled a bare "Status" beside
         * the other would be actively ambiguous (§44).
         *
         * `statusChip`, the same type as its sibling, for the same reason the sibling has it:
         * a fixed, colour-coded catalogue — of THREE here — where the chips ARE the design and
         * a search box over three items is friction.
         *
         * ⚠️ Three options, not the seven `getScanStatus` can return. See
         * `SCAN_STATUS_OPTIONS` for why the other four would be dead options on this surface.
         */
        headerName: 'Scan Status',
        type: 'statusChip',
        options: SCAN_STATUS_OPTIONS,
      },
      {
        field: FILTER_FIELDS.owner,
        headerName: 'Owner',
        type: 'string',
        options: ownerOptions,
        // The list-row owner shape (`name` / `email` / `avatar`), not the single-GET shape
        // (`first_name` / `last_name`) the certificates drawer searches — same entity, different
        // endpoint. `preview` picks the row renderer.
        searchParams: { fields: ['name', 'email'], preview: 'owners' },
        scanEntity: 'owner' as const,
      },
      {
        field: FILTER_FIELDS.product,
        headerName: 'Product',
        type: 'string',
        options: productOptions,
        searchParams: { fields: ['title', 'sku'], preview: 'products' },
        scanEntity: 'product' as const,
      },
      {
        field: FILTER_FIELDS.certificate,
        headerName: 'Certificate',
        type: 'string',
        options: certificateOptions,
        // `product.title` as a dot-path: a certificate's own row has no title, and searching a
        // list of uuids by uuid is not a thing anyone does. `id` stays so a pasted id still finds
        // its certificate.
        searchParams: { fields: ['product.title', 'id'], preview: 'certificates' },
        scanEntity: 'certificate' as const,
      },
      {
        field: FILTER_FIELDS.location,
        headerName: 'Location',
        type: 'location',
        options: locationOptions,
      },
      // ⚠️ The date range is deliberately NOT a `columns` entry, even though it now has a
      // drawer field (Task 3, VERA-260 — see `extraFilters` below for where it lives and
      // why). A `columns` entry becomes an `internalFilters` array member in `Filters.tsx`,
      // and THAT array is what drives the Filter button's applied-count badge
      // (`internalFilters.length`, `Filters.tsx`) and the "clear all" control's visibility —
      // both built for OPTIONAL narrowing facets that can legitimately be zero. The date
      // range is never zero (a range is always applied; see this file's own docblock and
      // `AnalyticsAppliedFilters`'s matching note on why it gets no chip either), so routing
      // it through that machinery would pin the badge at "2" and the clear-all control
      // permanently visible even with every OTHER dimension untouched — a standing false
      // signal on a page every user visits. `extraFilters` is the drawer's existing escape
      // hatch for exactly this shape of control ("Show archived" on Products uses it the
      // same way): a field that is real and editable but is not one of the optional facets
      // the badge counts.
    ],
    [ownerOptions, productOptions, certificateOptions, locationOptions],
  )

  /**
   * ── THE TOOLBAR SEARCH'S GROUPS — derived straight off `columns` above ─────────────────
   *
   * NOT a second declaration of which fields Owner / Product / Certificate search, or which
   * preview row renders them — that config already lives on `columns`, one entry per
   * dimension, and duplicating it here is exactly the drift §36 exists to prevent. This just
   * narrows `columns` to the three entity dimensions (Status and Location are not
   * typeahead-searchable entities) and reshapes them into what `AnalyticsSearchTypeahead`
   * needs: a label, the same `options`, and the same `searchParams`.
   */
  const SEARCH_GROUP_LABEL: Record<string, string> = {
    [FILTER_FIELDS.owner]: 'Owners',
    [FILTER_FIELDS.product]: 'Products',
    [FILTER_FIELDS.certificate]: 'Certificates',
  }
  const searchGroups = useMemo<AnalyticsSearchGroup[]>(() => {
    const entityFields: string[] = [FILTER_FIELDS.owner, FILTER_FIELDS.product, FILTER_FIELDS.certificate]
    return columns
      .filter(c => entityFields.includes(c.field))
      .map(c => ({
        field: c.field as AnalyticsSearchFilterField,
        label: SEARCH_GROUP_LABEL[c.field],
        options: c.options ?? [],
        searchParams: {
          fields: c.searchParams?.fields ?? [],
          preview: c.searchParams?.preview as 'owners' | 'products' | 'certificates',
        },
      }))
  }, [columns])

  /**
   * Stage a typeahead pick (or a resolved scan) into its filter — ADDING to whatever is
   * already applied, exactly like ticking the equivalent checkbox in the drawer would. This is
   * the whole point of routing search through the existing filters rather than a 6th
   * dimension: the resulting `certificateIds` / `ownerIds` / `productIds` array, and therefore
   * the applied-filter chip it produces, is indistinguishable from one the drawer set.
   */
  const applyEntityFilter = useCallback(
    (field: AnalyticsSearchFilterField, id: string) => {
      const add = (arr: string[]) => (arr.includes(id) ? arr : [...arr, id])
      switch (field) {
        case 'ownerIds':
          dispatch(setAnalyticsFilters({ ownerIds: add(filters.ownerIds) }))
          return
        case 'productIds':
          dispatch(setAnalyticsFilters({ productIds: add(filters.productIds) }))
          return
        case 'certificateIds':
          dispatch(setAnalyticsFilters({ certificateIds: add(filters.certificateIds) }))
          return
      }
    },
    [dispatch, filters.ownerIds, filters.productIds, filters.certificateIds],
  )

  /**
   * redux -> the `filters` array `Filters` stages from.
   *
   * Shape-matched to what `handleAddInternalFilter` produces (`{field, operator, value, id}`,
   * ids re-indexed) so the drawer's own equality check treats an echo of our own commit as
   * "nothing changed" and does not re-arm its debounce. Empty selections are omitted, which is
   * also what makes the toolbar's applied-count badge read correctly.
   *
   * The date range is NOT one of these six — see the `location` column's own comment in
   * `columns` above for why (routing it through this array would pin the Filter button's
   * applied-count badge at a permanent minimum of 2). It has its own always-on path;
   * see `AnalyticsDateRangeDrawerField` below.
   */
  const appliedFilters = useMemo(() => {
    const entries: { field: string; value: string[] }[] = [
      { field: FILTER_FIELDS.status, value: collapseStatuses(filters.certificateStatuses) },
      // No collapse/expand pair for this one: unlike the four transfer stages, no two scan
      // outcomes share a label, so the stored values and the chips are already 1:1.
      { field: FILTER_FIELDS.scanStatus, value: filters.scanStatuses },
      { field: FILTER_FIELDS.owner, value: filters.ownerIds },
      { field: FILTER_FIELDS.product, value: filters.productIds },
      { field: FILTER_FIELDS.certificate, value: filters.certificateIds },
      { field: FILTER_FIELDS.location, value: filters.location },
    ]
    return entries
      .filter(e => e.value.length > 0)
      .map((e, id) => ({ field: e.field, operator: 'isAnyOf', value: e.value, id }))
  }, [
    filters.certificateStatuses,
    filters.scanStatuses,
    filters.ownerIds,
    filters.productIds,
    filters.certificateIds,
    filters.location,
  ])

  /**
   * The applied set -> redux.
   *
   * Always writes all six keys, reading an absent field as "nothing selected". That is what
   * makes both clear paths work for free: the drawer's Clear and the toolbar's clear-X both
   * report `[]`, and every dimension resets from that one array without a separate
   * `onClearExtraFilters` callback.
   *
   * The date range is not one of these six — see the `location` column's own comment for
   * why it is never routed through `columns` / `appliedFilters` at all (the badge-count
   * problem). It has its own always-on write path: `AnalyticsDateRangeDrawerField` (rendered
   * via `extraFilters`, below) calls `dispatch(setAnalyticsFilters({ dateRange }))` directly,
   * the same way the toolbar's own `dateRangeControl` already does.
   */
  const handleFiltersChange = useCallback(
    (applied: any[]) => {
      const valueFor = (field: string): string[] => {
        const match = applied.find(f => f?.field === field)
        return Array.isArray(match?.value) ? (match.value as string[]) : []
      }
      dispatch(
        setAnalyticsFilters({
          certificateStatuses: expandStatuses(valueFor(FILTER_FIELDS.status)),
          scanStatuses: valueFor(FILTER_FIELDS.scanStatus),
          ownerIds: valueFor(FILTER_FIELDS.owner),
          productIds: valueFor(FILTER_FIELDS.product),
          certificateIds: valueFor(FILTER_FIELDS.certificate),
          location: valueFor(FILTER_FIELDS.location),
        }),
      )
    },
    [dispatch],
  )

  /**
   * ── THE BILLING PERIOD ────────────────────────────────────────────────────────────
   *
   * Read off the ROI response already in redux (`roi.spend`), NOT from
   * `useCompanySubscription`.
   *
   * Both ultimately read the same `company_subscriptions` row, so this is not a choice
   * between two sources of truth — it is a choice between the copy that DECIDED the
   * outcome and a second copy of the same columns. `getVeraSpend`
   * (`analytics.postgres.repository.ts`) computes `exact = from >= current_period_start
   * && to <= current_period_end` and then returns the very `current_period_start` /
   * `current_period_end` it compared against. A range built from those two values
   * therefore satisfies that predicate BY CONSTRUCTION, at any clock skew, on any stale
   * cache, whatever the other endpoint happens to be serving. Built from the subscription
   * endpoint's copy it would satisfy it only while the two agree, and the failure mode
   * when they do not is silent and exactly backwards: the amber approximation caveat
   * appears under the range chosen specifically to remove it.
   *
   * The concrete divergence to worry about is not clock skew but ROW CHOICE:
   * `getVeraSpend` takes `company_subscriptions ORDER BY created_at DESC LIMIT 1` INNER
   * JOINed to `stripe_subscription_products`, while `/subscriptions/current` resolves
   * "current" its own way. A company with more than one subscription row is all it takes
   * for the two to name different periods, and the symptom would be the caveat, not an
   * error.
   *
   * ── WHAT THIS DOES NOT BUY, stated plainly ───────────────────────────────────────
   *
   * NOT a saved request. Measured on a cold load of /analytics:
   * `GET /api/subscriptions/current` is already issued unconditionally by
   * `SidebarUserBlock`'s `useCurrentPlan`, so `useCompanySubscription` here would have
   * been free too. The two sources cost the same; correctness is the whole of the reason.
   *
   * NOT a single round of analytics requests, either. The page's queries are keyed on
   * `filters.dateRange` and are issued by the PARENT during its own render, before any
   * effect in this child can run — so whichever source supplies the boundaries, the
   * bootstrap window is fetched first and the billing window second (measured: `total` +
   * `roi` at the 30-day window, then both again at the billing window). Collapsing that to
   * one round means holding the first request until the boundaries are known, which can
   * only be done at the request site — see the report's note on `AnalyticsPage`.
   *
   * The one real cost of this source over the subscription hook is LATENCY: the ROI
   * response is the slowest request on the page, so the bootstrap numbers are on screen
   * slightly longer than they would be otherwise.
   */
  const spend = useSelector(state => state.analytics.roi?.roi?.spend)
  const periodStart = spend?.current_period_start
  const periodEnd = spend?.current_period_end

  /**
   * Applied EXACTLY ONCE, and never over a range anybody has chosen.
   *
   * Two independent guards, because they answer different questions:
   *
   *  - `appliedRef` answers "have I already done this in this mount?" It covers the window
   *    between the dispatch and the re-render that reflects it, and any effect re-run that
   *    still closes over the pre-dispatch selector value (React 18's development-mode
   *    double-invocation being the ordinary case).
   *  - The IDENTITY check against `BOOTSTRAP_DATE_RANGE` answers "has anything written
   *    `dateRange` yet?" That is the one that matters, because the boundaries can arrive
   *    LATE — the ROI request is the slowest on the page, and a user who picks "Last week"
   *    while it is in flight must not have it silently replaced a moment later. Reference
   *    equality is exact for this: the reducer only produces a new `dateRange` object when
   *    a writer supplies one (see `BOOTSTRAP_DATE_RANGE`'s own note).
   *
   * The ref is also SET on the not-bootstrap path, so a user who then clears back to some
   * other range is never revisited either. One shot, on first load, or not at all.
   *
   * Verified rather than argued: instrumented on a cold load of /analytics, the effect ran
   * three times (boundaries unknown → boundaries known → after the write) and dispatched
   * exactly ONCE.
   */
  const billingDefaultAppliedRef = useRef(false)

  useEffect(() => {
    if (billingDefaultAppliedRef.current) return

    // Someone has already set a range — the user, or the chart's "Apply to page". Stand down
    // permanently rather than waiting for a chance to overwrite them.
    if (filters.dateRange !== BOOTSTRAP_DATE_RANGE) {
      billingDefaultAppliedRef.current = true
      return
    }

    // Boundaries not known yet, or not knowable (no subscription, trial, never
    // provisioned). `billingPeriodDateRange` returns null rather than guessing, and the
    // page keeps the 30-day bootstrap window — which is the correct outcome, not a
    // fallback: a brand with no plan has no billing period to open on.
    const range = billingPeriodDateRange({
      current_period_start: periodStart,
      current_period_end: periodEnd,
    })
    if (!range) return

    billingDefaultAppliedRef.current = true
    dispatch(setAnalyticsFilters({ dateRange: range }))
  }, [dispatch, filters.dateRange, periodStart, periodEnd])

  /**
   * The preset list for THIS picker — the shared eight, plus "This billing period" once the
   * boundaries are known.
   *
   * `makeDateRangeShortcutItems`, not the module-level `dateRangeShortcutItems` constant:
   * the constant is what every table's filter drawer renders and has no company context to
   * offer, so the billing-period preset cannot live in it.
   *
* ── "All time" IS enabled here, floored on the company's own start ──────────────
   *
   * It used to be off, with a note recording that the two things which made it UNSAFE had
   * both been dealt with (invoice-backed spend removed the inflated denominator; the
   * prior-period comparison is suppressed when the window predates the company) and that
   * leaving it off was therefore "a product decision about this picker's option list".
   * That decision has been made: offer it.
   *
   * ⚠️ `dataFloor` is REQUIRED and is passed — `VERA_DATA_FLOOR` (2020-01-01) is the
   * "we were not told" fallback and is the wrong input for this page. It is `created_at`,
   * the same floor `RoiHero` already uses, so the picker's "All time" resolves to exactly
   * the window the hero states above it ("All time · since <date>"). A picker offering a
   * range the hero cannot agree with would be two different all-times on one screen.
   *
   * The ask was "since the company SUBSCRIBED". `created_at` is the closest honest value
   * currently on the client and it is safe rather than merely close: spend is counted from
   * settled invoices, so a floor earlier than the first invoice adds NO spend and cannot
   * inflate the denominator. A strict subscription/first-invoice floor would need that
   * date exposed on `IVeraSpend` (it holds `invoice_count` and `current_period_start`, but
   * no first-invoice instant) — worth doing, and a backend change rather than this one.
   */
  const shortcutItems = useMemo(
    () =>
      makeDateRangeShortcutItems({
        ...(periodStart && periodEnd
          ? { billingPeriod: { start: periodStart, end: periodEnd } }
          : {}),
        includeAllTime: true,
        dataFloor: company?.created_at ?? null,
      }),
    [periodStart, periodEnd, company?.created_at],
  )

  // Date range as dayjs values
  const dateRangeValue: DateRange<Dayjs> = [dayjs(filters.dateRange.from), dayjs(filters.dateRange.to)]

  /**
   * The name of the preset the CURRENT range corresponds to, if any — "This month",
   * "This billing period" — rendered ahead of the dates as `This month · Aug 1 – Aug 28`.
   *
   * BOTH, not one or the other. The name alone loses what the range resolved TO, which is
   * the thing every number on the page was actually measured over and the thing a reader
   * needs when they screenshot it; the dates alone lose what was ASKED for, which is what
   * makes the field scannable at a glance.
   *
   * Recomputed from the value on every render rather than remembered from the click — see
   * `matchDateRangeShortcut`. A remembered label survives a hand-edited calendar selection
   * and then names a range that is no longer that preset, and a field asserting "This
   * month" over some other month is worse than a field asserting nothing.
   */
  const presetLabel = useMemo(
    () => matchDateRangeShortcut(dateRangeValue, shortcutItems),
    // The dependencies are the underlying redux VALUES, not `dateRangeValue` — that array is
    // rebuilt on every render, so listing it would recompute the match every pass. The dayjs
    // objects inside it are derived from exactly these two Dates and nothing else.
    [filters.dateRange.from, filters.dateRange.to, shortcutItems],
  )

  /**
   * TIME IS SHOWN ONLY WHEN THERE IS A TIME TO SHOW.
   *
   * The picker is now a `DateTimeRangePicker`, so a range can be scoped to the hour — but
   * the field renders the clock only once the range stops sitting on day boundaries. Both
   * ends together (`Aug 3, 2026 3:13 PM – Aug 27, 2026 11:59 PM`) is ~44 characters, and a
   * brand asking "how did last month go" is asking about days; spending that width on
   * `12:00 AM – 11:59 PM` says nothing.
   *
   * Derived from the VALUE, not from local "did the user open the clock" state. The range is
   * also written by the chart's "Apply to page" promote path, which produces a
   * millisecond-precise window (`nextBucket - 1ms`) — genuinely timed, and it must render as
   * such no matter which control set it. State would have missed that; the value cannot.
   */
  const wholeDay = isWholeDayRange(dateRangeValue)

  /**
   * ── A NAMED RANGE SHOWS DATES, EVEN WHEN ITS VALUE CARRIES A CLOCK ────────────────
   *
   * 🚨 THIS CHANGES THE DISPLAY ONLY. The VALUE keeps its time component, and that is not a
   * detail: `getVeraSpend` decides the ROI hero is exact with
   * `from >= current_period_start`, and a Stripe period starts at the subscription's own
   * instant, not at midnight. Day-flooring the VALUE to make the field read as dates would
   * push `from` before the period start and hand back the very approximation caveat that
   * defaulting to the billing period exists to remove — silently, with the page still
   * showing the range the user asked for. Display and value are separable here. Keep them
   * separate; do not "simplify" this by rounding `filters.dateRange`.
   *
   * WHY A PRESET IS ALLOWED TO HIDE ITS CLOCK. `isWholeDayRange` asks "does this range have
   * a time worth reading?", and for a preset the answer is no even when the instants are not
   * midnight — the boundary was not chosen by the user, it is a property of the thing the
   * preset NAMES. "This billing period · Jul 4, 2026 – Aug 3, 2026" is complete: the label
   * says what the window is, the dates say which days it spans, and `7:12 AM` on both ends
   * is Stripe's bookkeeping, not information the reader can act on. It also costs ~140px of
   * field width to say it, on the DEFAULT range, on every page load.
   *
   * The clock therefore survives exactly where it was actually chosen: a hand-picked or
   * typed range, a window promoted from the chart's brush (millisecond-precise), and
   * "Last 24 hours" — which is deliberately unmatchable (see `matchDateRangeShortcut`) and
   * so keeps both its times and its width.
   */
  const showTime = !wholeDay && !presetLabel

  const handleDateRangeChange = (newValue: DateRange<Dayjs>) => {
    if (newValue[0] && newValue[1]) {
      dispatch(
        setAnalyticsFilters({
          dateRange: { from: newValue[0].toDate(), to: newValue[1].toDate() },
        }),
      )
    }
  }

  /**
   * ── THE DATE RANGE, IN THE TOOLBAR ROW ───────────────────────────────────────────────
   *
   * 🚨 IT USED TO OWN A ROW OF ITS OWN, DIRECTLY ABOVE THE ACTION LINE, and that row is
   * gone. The history matters because the row was never designed — it was a RESIDUE. It
   * originally held the Scans / Certificates toggle beside the date picker; when the
   * toggle was demoted into the two panel headers (it scoped only those two cards — see
   * the note above the imports), the date picker was left alone on a full-width line with
   * nothing beside it, which reads as an orphan rather than as a deliberate first-class
   * control.
   *
   * It now renders in `ActionLine`'s `afterSearchComponents` slot, so the page has ONE
   * control band: search · scan · date, then Download / Views / Layout / Filter.
   *
   * ⚠️ AFTER search, not before it, and that is a user directive rather than a detail. The
   * first placement led the left group on a scope → narrow → act reading; it was overruled
   * because search is the toolbar's fixed landmark — same control, same x, on ~30 pages —
   * and this field's width is a function of its own value (a preset shows ~280px of dates, a
   * timed range ~420px), so putting it first makes the landmark start somewhere different
   * depending on what the date happens to say. See the slot's own note in `ActionLine`.
   *
   * ## WHY THIS IS WHAT MAKES THE ROI HERO'S FIXED "ALL TIME" HONEST
   *
   * This is half of a pair and does not stand alone. The hero's own period picker was
   * removed in the same change (`RoiHeroPeriod`), leaving the card scoped to a stated,
   * fixed All time. On its own that removal would have been a REGRESSION of exactly the
   * kind this page just finished fixing: a page-level-looking date control sitting ABOVE
   * the topmost card while not governing it is the same false statement about scope that
   * got the Scans / Certificates toggle moved out of here in the first place, and it is
   * the kind of false statement a reader never checks.
   *
   * Moving the picker DOWN into the toolbar dissolves that objection rather than merely
   * coexisting with it. The order on the page is now:
   *
   *     title → ROI hero (states All time) → toolbar (date + search + controls)
   *           → applied-filter chips → card grid
   *
   * so the date control is BELOW the hero and immediately ABOVE the content it actually
   * scopes. Its reach reads as "everything beneath me", which is true — and the hero,
   * being above it, is visibly outside that reach. Neither change is safe without the
   * other; do not revert one of them in isolation.
   *
   * Extracted to a variable rather than written inline at the call site so this reasoning
   * has somewhere to live and the `ActionLine` invocation below stays readable.
   */
  const dateRangeControl = (
    <>
      {/* Same chrome as the drawer's date filter, from the shared module rather than a second
            set of hand-written `sx` — 49px CONTROL_HEIGHT band, fieldChrome fill/border, the
            brand-purple range calendar and the shortcut presets (§36). */}
        {/* The field widens only when it is actually showing a time. Reserving the timed
            width permanently would leave ~140px of empty field in the common case, and the
            shift only ever follows a deliberate act by the user (setting a time, or
            promoting a chart brush window), so it is explained by the gesture that caused
            it rather than appearing unprompted.

            CONTENT-SIZED, rather than a fixed 280/420 on this Box — the field is the sum of
            its parts and a preset name adds precisely its own rendered width on the left.

            The DATES are content-sized too now. `dateRangeTextWidth(!showTime)` is no longer
            "280/420 minus the insets" — that number described the superseded fixed-width
            field and left ~51px of dead space to the right of a typical range, which is what
            was reported as too much right padding. It is now the measured widest value the
            format can produce, and it applies only as the fallback: the input takes its own
            text's width wherever `field-sizing` is supported. Measured in the browser rather
            than asserted; see `dateRangeFieldWithIconSx`. */}
        <Box sx={{ display: 'flex' }}>
          <DateTimeRangePicker
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            open={pickerOpen}
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            /**
             * `maxDateTime`, NOT `disableFuture` — the two differ in granularity and only one
             * of them is compatible with a day-boundaried range.
             *
             * `disableFuture` on a DATE picker compares by day (`isAfterDay`), so "today" is
             * always valid. On a DATE-TIME picker it additionally compares the INSTANT, so
             * `today 11:59:59 PM` — which is exactly what every day-boundaried preset and the
             * whole-day default produce — is "in the future" and the field renders in its
             * error state. Capping at the end of today keeps the future genuinely
             * unselectable while leaving the whole of today reachable.
             */
            maxDateTime={dayjs().endOf('day')}
            /**
             * The written-month shape (`Aug 3, 2026`), with the clock appended only when the
             * range carries one AND was not produced by a named preset — see `showTime`
             * above, and the format constants' own note on why an ordinal ("Aug 3rd") was
             * rejected.
             *
             * On the PICKER, not in `slotProps.field`. The `field` slot's props are typed
             * against the picker's DEFAULT field — the multi-input one — regardless of the
             * single-input component actually passed to `slots.field`, so anything set there
             * is a type error even though it reaches the right component at runtime. These
             * two are first-class picker props that the picker forwards to whichever field
             * it renders, which is both correct and typed.
             */
            format={showTime ? DATE_TIME_RANGE_FIELD_FORMAT : DATE_RANGE_FIELD_FORMAT}
            slots={{
              // `SingleInputDateTimeRangeField` with the section padding off — see the
              // component's own note for why that setting cannot live on a prop here.
              field: UnpaddedDateTimeRangeField,
              day: DateRangePickerDay,
              calendarHeader: DateRangeCalendarHeader,
            }}
            slotProps={{
              desktopPaper: { sx: dateRangeDesktopPaperSx },
              layout: { sx: { p: 4, borderRadius: 3 } },
              // No `field` entry: `fullWidth` on it was redundant with `textField.fullWidth`
              // below (the single-input field renders that TextField), and the slot's props
              // are typed against the picker's default MULTI-input field, so setting
              // anything there is a type error for no behavioural gain.
              day: { sx: dateRangeDaySx },
              textField: {
                size: 'small',
                // NOT `fullWidth`. The control sizes to its content now (see the Box above):
                // `fullWidth` would stretch the FormControl to 100% of a parent whose width
                // is being derived from this element, which is the one arrangement in which
                // the dates' fixed box stops being the thing that decides.
                placeholder: 'Date range',
                // The field IS the open affordance (there is no adornment button) — this is the
                // other half of `pickerOpen`. The shared chrome already draws `cursor: pointer`
                // on the input, which was advertising a click that did nothing.
                onClick: () => setPickerOpen(true),
                /**
                 * A LEADING CALENDAR GLYPH, so the field reads as a date control before it is
                 * read at all — without it this is a wide box of plain text sitting between a
                 * segmented control and a toolbar, and nothing about its shape says "dates".
                 *
                 * A CALENDAR, not a clock, and that follows from the control rather than from
                 * taste: this is a `DateTimeRangePicker` whose primary act is picking DAYS —
                 * every named preset reads as days, and the field hides the clock entirely
                 * unless the user actually picked one (see `showTime`). A clock glyph would
                 * name the exception.
                 *
                 * The geometry is the whole trick, and it is in `dateRangeFieldWithIconSx`:
                 * that variant moves the field's chrome from the inner `input` to the ROOT so
                 * this adornment can be an ordinary in-flow flex item, which is what lets the
                 * preset label sit beside the glyph and size itself. Read that export before
                 * changing anything here.
                 */
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Stack
                        direction="row"
                        alignItems="center"
                        // Explicit px, not a `spacing` factor. `@repo/ui`'s 4px base and
                        // MUI's 8px default disagree (§29), and this gap has to equal the
                        // one the field's own geometry is stated in.
                        sx={{ gap: `${DATE_FIELD_GAP}px` }}
                      >
                        {/* `strokeWidth={1}` — house lucide weight (§21). */}
                        <CalendarIcon size={DATE_FIELD_ICON_SIZE} strokeWidth={1} />
                        {/**
                          * The preset NAME, when the current range is exactly one.
                          *
                          * SAME SIZE as the dates, distinguished by COLOUR alone. `body2` is
                          * 16px in this theme (measured — not MUI's 14px default; `body3` is
                          * the 14px step), and the adornment's own `color:
                          * theme.palette.text.secondary` puts the label at rgb(161,161,170)
                          * against the input's rgb(250,250,250).
                          *
                          * A smaller label was tried in the reasoning and rejected: the
                          * hierarchy it would express — dates are the VALUE, the name is the
                          * QUALIFIER — is already carried by the colour, and two type sizes
                          * sharing one baseline inside a 49px control reads ragged. The width
                          * saving would have been ~15px on the longest label, which is not
                          * worth a second font size in a field this small.
                          *
                          * `whiteSpace: nowrap` because the field is one 49px line: a label
                          * allowed to wrap would push the input out of the box rather than
                          * shortening itself.
                          */}
                        {presetLabel && (
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {presetLabel} ·
                          </Typography>
                        )}
                      </Stack>
                    </InputAdornment>
                  ),
                },
                // `!showTime`, not `wholeDay` — the text box has to be sized for the format
                // actually being rendered. A named range showing the compact form gets the
                // compact box, which is what takes the default field back to ~280px + label.
                sx: (theme: Theme) => dateRangeFieldWithIconSx(theme, dateRangeTextWidth(!showTime)),
              },
              shortcuts: {
                changeImportance: 'set',
                // The company-aware list — see `shortcutItems` above for what is added and
                // what is deliberately withheld.
                items: shortcutItems,
                dense: false,
                sx: dateRangeShortcutsSx,
              },
              popper: { sx: { zIndex: 1400 } },
            }}
          />
        </Box>
    </>
  )

  /**
   * ── CERTIFICATE STATUS, COLLAPSED FOR THE TOOLBAR TRIGGER ────────────────────────────
   *
   * The SAME `collapseStatuses` / `expandStatuses` pair the drawer's own `StatusChipFilter`
   * adapter uses (`appliedFilters` / `handleFiltersChange` above) — one conversion, two
   * access paths, per `AnalyticsStatusToolbarFilter`'s own docblock.
   */
  const statusValue = useMemo(
    () => collapseStatuses(filters.certificateStatuses),
    [filters.certificateStatuses],
  )
  const handleStatusChange = useCallback(
    (next: string[]) => dispatch(setAnalyticsFilters({ certificateStatuses: expandStatuses(next) })),
    [dispatch],
  )

  /**
   * ── SCAN STATUS ──────────────────────────────────────────────────────────────────
   *
   * No collapse/expand step, and that asymmetry with the sibling above is real rather than
   * an oversight: `collapseStatuses` exists because four certificate transfer stages share
   * the label "Transfer", so one chip has to stand for four stored values. The three scan
   * outcomes have three distinct labels, so the stored array IS the chip selection.
   */
  const handleScanStatusChange = useCallback(
    (next: string[]) => dispatch(setAnalyticsFilters({ scanStatuses: next })),
    [dispatch],
  )

  /**
   * ── THE FILTER BUTTON'S BADGE COUNTS WHAT IS *BEHIND* THE BUTTON ──────────────────
   *
   * `Filters` counts `internalFilters.length` by default, which is right for every other
   * page in the app: their filters live ONLY in the drawer, so "3 applied" and "3 behind
   * this button" are the same number. This page is the exception — it is the only one that
   * promotes drawer fields into first-class toolbar controls — and there the two numbers
   * come apart in the direction that misleads.
   *
   * Reported: with Certificate status and Scan status BOTH visible in the toolbar and both
   * applied, the badge read `2` while the two chips sat an inch to its left. A badge on a
   * closed panel is read as a claim about what the panel is HIDING, so `2` said "two more
   * filters in here" when the answer was zero. Verified in the browser at a 1920 viewport:
   * one scan status selected, its chip visible in the toolbar, badge `1`.
   *
   * ⚠️ THE FIX IS NOT TO REMOVE THOSE FIELDS FROM THE DRAWER. That was the first proposal
   * and it fails on the same axis the whole staged-drop mechanism turns on: the toolbar
   * controls are WIDTH-CONDITIONAL, so a drawer built from "whatever the toolbar is not
   * showing" would have viewport-dependent CONTENTS — at 1900px it would open EMPTY, and
   * the one canonical, complete list of what this page can be narrowed by would cease to
   * exist. The drawer stays complete. Only the COUNT changes, because only the count is
   * making a claim about visibility.
   *
   * ── HOW THIS READS THE ANSWER, AND WHY IT NO LONGER RE-STATES THE THRESHOLD ──────
   *
   * It used to be two `useMediaQuery` calls against the same constants the drop rules used.
   * That could not survive the move to container queries (VRG-517): `matchMedia` has no
   * container form, so a viewport media query here would disagree with the CSS in exactly
   * the band the container query was introduced to fix — sidebar expanded, viewport past
   * the threshold, control hidden by CSS but still counted as visible here, leaving an
   * applied filter uncounted while only its chip showed it.
   *
   * So this asks the DOM what the CSS decided, rather than re-deciding it: it reads the
   * computed `display` of the drop wrappers themselves. There is no second threshold to
   * drift, because there is no second threshold — the container query is the only place a
   * number appears, and this is a readback of its result.
   *
   * The measurement runs in a `useLayoutEffect` with NO dependency array, so every commit
   * re-reads it, and the `prev === next` guards mean a stable answer sets no state and
   * there is no loop. That is the same pairing `useMeasuredHeight` in `AnalyticsPage` uses
   * and for the same reason: the `ResizeObserver` below is the more direct instrument and
   * the only thing that catches a width change with no React commit behind it (the sidebar
   * expanding, which is the case this whole change is about) — but it fires ZERO times in
   * the Claude preview pane, so it cannot be the only mechanism or none of this is
   * verifiable there.
   *
   * ⚠️ HONEST LIMIT: the observer's contribution specifically — the badge re-counting when
   * the band changes width with no other React work happening — is NOT exercisable in the
   * preview pane, because no observer or resize callback fires there at all. It is the
   * same limitation the previous `useMediaQuery` version had (its `change` event does not
   * fire in the pane either, verified), so this is not a new gap; the every-commit read is
   * what actually carries the common cases.
   *
   * The date range is absent from this set because it is absent from the count already —
   * it is an `extraFilter`, never an `internalFilters` member (see the `columns` array).
   */
  const toolbarRef = useRef<HTMLDivElement>(null)
  const certStatusDropRef = useRef<HTMLDivElement>(null)
  const scanStatusDropRef = useRef<HTMLDivElement>(null)
  const [certStatusInToolbar, setCertStatusInToolbar] = useState(false)
  const [scanStatusInToolbar, setScanStatusInToolbar] = useState(false)

  const readToolbarVisibility = useCallback(() => {
    const shown = (el: HTMLElement | null) =>
      !!el && window.getComputedStyle(el).display !== 'none'
    const cert = shown(certStatusDropRef.current)
    const scan = shown(scanStatusDropRef.current)
    setCertStatusInToolbar(prev => (prev === cert ? prev : cert))
    setScanStatusInToolbar(prev => (prev === scan ? prev : scan))
  }, [])

  useLayoutEffect(readToolbarVisibility)

  useLayoutEffect(() => {
    window.addEventListener('resize', readToolbarVisibility)
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(readToolbarVisibility) : null
    if (observer && toolbarRef.current) observer.observe(toolbarRef.current)
    return () => {
      window.removeEventListener('resize', readToolbarVisibility)
      observer?.disconnect()
    }
  }, [readToolbarVisibility])
  /**
   * True for a staged filter the reader can only reach by OPENING the drawer.
   *
   * Owner / Product / Certificate / Location have no toolbar control at any width, so they
   * always count. The two status axes count only while their own control has dropped —
   * each against ITS OWN threshold, because the drop is staged and there is a width band
   * (1700–1890) where scan status has dropped and certificate status has not.
   */
  const countFilterBehindDrawer = useCallback(
    (filter: { field?: string }) => {
      if (filter?.field === FILTER_FIELDS.status) return !certStatusInToolbar
      if (filter?.field === FILTER_FIELDS.scanStatus) return !scanStatusInToolbar
      return true
    },
    [certStatusInToolbar, scanStatusInToolbar],
  )

  return (
    <>
      {/* THE PAGE'S ONE CONTROL BAND — the standard data-table action bar, so this page's
          date / search / filter / download behave identically to every table in the
          platform. The five filter dimensions are real `columns`, so the drawer renders its
          OWN fields for them (§28) — the same chips, search and clear behaviour every other
          list gets — instead of the hand-rolled selects that used to be passed through
          `extraFilters`.

          ⚠️ There is no longer a row above this one. See `dateRangeControl` for why the date
          picker moved in here and why that move is what licenses the ROI hero's fixed
          All time. Do not reintroduce a page-level control row above the hero. */}
      <ActionLine
        ref={toolbarRef}
        /**
         * ⚠️ NO `sx={toolbarContainerSx}` HERE ANY MORE (VRG-535). It used to be passed from
         * this call site, on the reasoning that keeping the container out of `ActionLine`
         * left the ~30 other pages byte-identical because "no other page has controls that
         * drop". That premise expired the moment the ordinary table toolbar gained its own
         * label collapse: every page now has controls that drop, so `ActionLine` declares
         * the container itself and this prop would be a second declaration of the same thing
         * on the same element.
         *
         * Harmless if reinstated — the values are identical — but it would read as though
         * Analytics needed something the other toolbars do not, which is exactly backwards.
         */
        /**
         * SEARCH IS `AnalyticsSearchTypeahead`, NOT `ActionLine`'s own built-in `Search` field
         * (VERA-260 — Platform.Analytics.Overview).
         *
         * The toolbar used to render the generic field bound to local `search` state that no
         * query ever read, with the NFC scan button beside it (`showScan`, `scanEntity`)
         * dumping the raw scanned TAG id straight into that dead box via `ActionLine`'s own
         * built-in effect. `showSearch={false}` / no `showScan` here turns that off entirely —
         * the typeahead below is a REPLACEMENT for the slot, not an addition beside it.
         *
         * It renders in `afterSearchComponents` (the left group's trailing field slot) rather
         * than `showSearch` because it needs its own Popover dropdown, which the shared
         * `Search` field cannot host. Grouped by Owner / Product / Certificate, it stages
         * picks into the SAME `ownerIds` / `productIds` / `certificateIds` filters the drawer's
         * own fields write (`applyEntityFilter`) — search is a fast path INTO the filters that
         * already exist, not a sixth independent dimension. Its own scan button resolves a
         * scanned tag to the CERTIFICATE it belongs to (not the raw tag id) and applies that
         * the same way a click would; see the component's own docblock for the full design,
         * including why it uses a dedicated scan target key rather than `ActionLine`'s shared
         * one.
         */
        showSearch={false}
        afterSearchComponents={
          <>
            <AnalyticsSearchTypeahead groups={searchGroups} onSelect={applyEntityFilter} />
            {/* Order is search/scan -> date -> status (user directive, VERA-260). This
                MATCHES the applied-filter chip row below the toolbar (date chip always
                first — `AnalyticsAppliedFilters`), but it does NOT fully match the drawer's
                own field order: `Filters.tsx` always renders `extraFilters` (where the date
                range's drawer field lives — see `extraFilters` prop below) AFTER every
                `columns` field, so in the drawer date ends up LAST (after Location) rather
                than first, with no way to reorder that from here. Known, reported, not
                silently different — see the end-of-run report for the full explanation.
                Both toolbar controls DROP (not wrap), in STAGES — status first, then the date —
                see that constant's own note for why, and why both share it. */}
            {/* STAGED: status drops first; the date is held to a narrower width because it
                scopes every number on the page while status narrows one facet. */}
            <Box sx={TOOLBAR_DATE_DROP_SX}>{dateRangeControl}</Box>
            {/* ⚠️ "VIEW BY" USED TO SIT HERE, and it is not coming back to this row.

                It was a page-level control writing `filters.granularity`, and the reach was
                wrong: measured on a canvas holding a Scans chart and a Certificates chart,
                one change fired two requests and re-bucketed both. The bucket is a DISPLAY
                property of one card — the same class of question as its line/bar mark — so
                it now renders in each chart card's header beside that toggle, keyed by card
                id (`AnalyticsCardGranularityControl` / `analyticsCardGranularity`).

                That deleted a whole mechanism with it: a 1904px stage of its own
                (`TOOLBAR_GRANULARITY_MIN_WIDTH_PX`, above every laptop), its inverse
                `granularityChipInverseSx`, and the working fallback chip the chip row had to
                render because the toolbar control was never on screen at the width this page
                is verified at. A per-card control has no responsive stage to fall out of.
                The three constants below are the ORIGINAL three and did not move — the
                granularity stage dropped first precisely so every configuration beneath it
                stayed byte-identical, which is what made its removal a single deletion. */}
            <Box ref={certStatusDropRef} sx={TOOLBAR_STATUS_DROP_SX}>
              <AnalyticsStatusToolbarFilter value={statusValue} onChange={handleStatusChange} />
            </Box>
            {/* SCAN STATUS, immediately beside its sibling — the adjacency is what makes
                the two status axes legible as a pair rather than as one ambiguous "Status"
                (§44). It drops FIRST of the three, at the widest threshold: it narrows one
                facet on one card, where certificate status narrows one facet page-wide and
                the date scopes every number. Its drawer field and its applied-filter chip
                are the fallback, exactly as they are for the other two. */}
            <Box ref={scanStatusDropRef} sx={TOOLBAR_SCAN_STATUS_DROP_SX}>
              <AnalyticsScanStatusToolbarFilter
                value={filters.scanStatuses}
                onChange={handleScanStatusChange}
              />
            </Box>
          </>
        }
        showBulkActions={false}
        showSort={false}
        showLayoutOptions={false}
        showFilters
        columns={columns}
        // Options are supplied per column, so the drawer never needs to derive facets from rows.
        rows={[]}
        filters={appliedFilters}
        onFiltersChange={handleFiltersChange}
        filterOpenRequest={filterOpenRequest}
        /**
         * The date range's drawer fallback (Task 3, VERA-260) — NOT a `columns` entry; see
         * that array's own comment (on the `location` entry) for why. `extraFilters` renders
         * AFTER every `columns` field in `Filters.tsx` (fixed by that shared component, used
         * by ~30 pages — not reorderable from here), so this is the one field that cannot be
         * placed first in the drawer the way it is in the toolbar; see this file's end-of-run
         * report for that specific, known ordering gap.
         */
        /* Date range FIRST in the drawer, matching the toolbar's order. It is an `extraFilter`
           only for a mechanical reason (an always-applied facet in `columns` would pin the
           Filter badge's count above zero permanently) — that should not also banish it to
           the bottom of the drawer. */
        extraFiltersPosition="before"
        extraFilters={
          <>
            <AnalyticsDateRangeDrawerField
              value={filters.dateRange}
              onChange={range => dispatch(setAnalyticsFilters({ dateRange: range }))}
              shortcutItems={shortcutItems}
            />
            {/*
              THE DRAWN AREA (VERA-260). It had an applied-filter chip and no drawer
              presence at all, which made the drawer an incomplete answer to "what is
              narrowing this page" — see the component's own docblock for why it reports
              and clears rather than edits, and why it renders nothing when no ring is set.

              An `extraFilter` for the same mechanical reason the date range is: `area` is a
              `GeoPoint[] | null`, not the `string[]` every `internalFilters` member is, so
              the drawer's staged `{field, operator, value}` machinery has nothing to do
              with it. It rides in the SAME slot because `extraFilters` is one node — which
              also means it inherits `extraFiltersPosition="before"` and sits directly under
              the date range rather than beside the country `Location` field it shares a
              label with. Accepted: `Filters.tsx` renders `extraFilters` as one block, fixed
              by a shared component ~30 pages use, and there is no per-field ordering to opt
              into from here.
            */}
            <AnalyticsAreaDrawerField
              area={filters.area}
              onClear={() => dispatch(setAnalyticsFilters({ area: null }))}
            />
          </>
        }
        /* The badge counts only what is actually behind the button — see
           `countFilterBehindDrawer` for why this page needs an override and why the
           alternative (removing toolbar-visible fields from the drawer) was rejected. */
        countFilter={countFilterBehindDrawer}
        beforeFiltersComponents={beforeFilters}
        afterFiltersComponents={afterFilters}
        rightComponents={
          /*
            ── GATED ON `analytics_export`, DISABLED RATHER THAN HIDDEN (VERA-260) ──────

            🚨 THE PERMISSION ALREADY EXISTED AND NOTHING READ IT. `analytics_export`
            ("Export analytics") has been in the catalogue and the permission GROUPS since
            they were written, and is granted to `company_admin` — but
            `grep -rn "analytics_export" apps/platform/src` returned exactly ONE hit, the
            union in `types/Profile.ts`. So the role editor offered a toggle that changed
            nothing, which is worse than an ungated button: it is an access control the
            product claims to have.

            That went unnoticed for a defensible reason — until now the button wrote four
            hardcoded KPI figures. It now exports every card on the canvas, including the
            scan map's individual located events (owner-adjacent data, one row per scan),
            so it is squarely the kind of capability §24 says must be gated.

            DISABLE, not hide (§27/§46): this is an in-context ACTION on a page the user
            can already see, so the control stays visible and explains itself. Resolution
            is owned by someone else — an admin toggling `Export analytics` in the role
            editor — which is exactly the case where a tooltip naming the permission is the
            only channel that tells the user what to ask for. `PermissionGate`'s default
            message does that verbatim.

            NO new permission was invented here, so there is no §24 default-grants question
            outstanding: the grants are whatever the role editor already carries.

            ⚠️ A PLAIN BLOCK COMMENT, NOT A BRACED JSX ONE. This sits inside a JSX
            EXPRESSION container (`rightComponents={…}`), not among JSX children, and the
            braced form is only valid in the latter — here it parses as an empty object
            literal followed by a stray element and takes the whole file's JSX down with it.
          */
          <PermissionGate permission="analytics_export">
            {/*
              ⚠️ A TOOLTIP NESTED INSIDE THE GATE, AND THE NESTING IS SAFE RATHER THAN
              MERELY TOLERATED. The label below collapses to icon-only as the toolbar
              narrows (VRG-512), so the collapsed button needs a hover affordance naming
              it — but `PermissionGate` ALSO renders a tooltip of its own when access is
              denied, and two live tooltips on one target is the double-tooltip defect
              `ViewsDropdown` warns about.

              They cannot both be live, which is what makes this correct: `PermissionGate`
              returns its child UNTOUCHED when access is granted (no tooltip of its own,
              so this one is the only one), and when access is denied it clones the child
              with `disabled` — and a disabled MUI button has `pointer-events: none`, so
              THIS tooltip cannot fire and only the gate's permission message does. That
              is the same fact the gate's own `<span>` wrapper exists for.

              `enterDelay={600}` matches `ActivityDownload` and `LayoutOptions`, so the
              whole toolbar row answers to one delay.
            */}
            <Tooltip title="Download" enterDelay={600}>
            <Button
              size="large"
              variant="secondary"
              disableElevation
              onClick={onDownload}
              loading={downloadBusy}
              // The accessible name. The visible label below is `display: none` at a
              // narrow container, which takes it out of the accessibility tree as well as
              // the layout, so the collapsed button would otherwise announce as unnamed.
              aria-label="Download"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <DownloadIcon size={18} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                {/*
                  Label collapses to icon-only as the TOOLBAR CONTAINER narrows — FIRST of
                  the row to go, on the shared `download` rung (VRG-535,
                  `toolbarCollapse.ts`).

                  🚨 IT WAS A VIEWPORT QUERY AT `md`, AND THAT IS THE BUG (VRG-512). `md`
                  is 900px of VIEWPORT; the sidebar is a 288px flex sibling rather than an
                  overlay, so at the 1280 width this was reported at the viewport cleared
                  `md` comfortably while the toolbar container was ~943px and the row had
                  already wrapped to two rows. This button and Analytics' Layout button
                  were the last two controls in the app still on the viewport query that
                  VRG-535 removed from the other five.

                  IT SHARES THE `download` RUNG with the Table toolbar's export button and
                  `ActivityDownload` — same role, same position in the row — so all three
                  Download buttons in this app drop their label at the same width.

                  No `toolbarStartIconSx` counterpart: the icon and label are siblings of
                  this `Stack`, whose `spacing` emits the gap as a `margin-left` on the
                  LABEL, so hiding the label removes its gap with it. Only MUI's
                  `startIcon` leaves a margin behind on the icon itself.
                */}
                <Typography variant="body2" sx={toolbarLabelSx('download')}>
                  Download
                </Typography>
              </Stack>
            </Button>
            </Tooltip>
          </PermissionGate>
        }
      />
    </>
  )
}
